// English source copy for WA-adjacent public pages. Keys are grouped per
// page; other locales must define exactly the same keys (enforced by
// src/__tests__/i18nLocales.test.ts).
const en = {
  chatMeeting: {
    prompt: '{{name}} asked to meet. Meet?',
    yes: 'Yes, offer times',
    decline: 'Decline',
    notInterested: 'Not interested',
    error: "Couldn't save that. Try again.",
  },
  join: {
    title: "You've got access to an AI Secretary",
    titleThanks: '(thanks to {{name}})',
    scanQr:
      'Scan the QR code with your phone to start a WhatsApp conversation with your new secretary',
    fromThere:
      "From there, you'll be able to start setting up your account & using your secretary to handle your admin!",
    openWhatsApp: 'Open WhatsApp',
    inviteOnlyTitle: 'Dream is invite-only at the moment.',
    invalidBody:
      "This invite link isn't valid — it may have been mistyped or is no longer active. Ask the person who invited you for a fresh link, or join our waitlist and we'll be in touch.",
    joinWaitlist: 'Join the waitlist',
    loading: 'Loading',
  },
  connect: {
    statusAlreadyConnected:
      'This account is already connected. You can add another account below.',
    statusExpired:
      'This link has expired. Please ask your secretary for a new one.',
    statusInvalidToken:
      'This link is not valid. Please ask your secretary for a new one.',
    statusAlreadyLinkedOther:
      'That account is already linked to another Dream account.',
    statusError: 'We could not load your connect page. Please try again.',
    missingToken:
      'Missing onboarding token. Please open this page from WhatsApp.',
    loadAccountsError: 'Unable to load connected accounts right now.',
    disconnectError: 'Unable to disconnect this account.',
    yourAccount: 'Your account',
    title: 'Connect your account',
    body1:
      'Connect your email and calendar so your secretary can keep on top of your tasks, and watch for calendar clashes and conflicts.',
    body2: "They'll never send an email from your account.",
    allDone: 'All done?',
    youCan: 'You can',
    returnToWhatsApp: 'return to WhatsApp',
    keepConnecting: ', or keep connecting accounts',
    loadingAccounts: 'Loading your connected accounts…',
    connectedAccount: 'Connected account',
    disconnectAria: 'Disconnect {{account}}',
    thisAccount: 'this account',
    connectGoogle: 'Connect your Google account',
    connectAnotherGoogle: 'Connect another Google account',
    connectOutlook: 'Connect your Outlook account',
    connectAnotherOutlook: 'Connect another Outlook account',
    disclaimer: 'We support Gmail, Google Workspace and Outlook accounts.',
  },
  travelOptions: {
    title: 'Travel options',
    eyebrow: 'Your trip',
    tabs: 'Travel options',
    flights: 'Flights',
    hotels: 'Stays',
    trains: 'Trains',
    flight: 'Flight',
    hotel: 'Stay',
    train: 'Train',
    removeTask: 'Remove task',
    book: 'Book',
    bookWithDream: 'Book with Dream',
    separateTickets: 'Separate tickets',
    separateTicketsWarning:
      'These flights are booked separately. Check the connection time and allow for delays.',
    seeReturnTimes: 'See return times',
    otherReturnTimes: 'Other return times ({{count}})',
    bookBothFlights: 'Book both flights',
    viewStay: 'View stay',
    loading: 'Loading travel options…',
    close: 'Close',
    outbound: 'Outbound',
    return: 'Return',
    returnsErrorTitle: "We couldn't load your return flights",
    returnsErrorBody: 'Try again, or go back to explore other options',
    returnsRateLimited: 'Too many requests on this page. Wait a few minutes.',
    tryAgain: 'Try again',
    back: 'Back',
    returnRefreshCountdown: 'Prices will refresh in {{time}}',
    flightUnavailable: 'This flight is no longer available. Choose another.',
    gettingLivePricesButton: 'Getting live prices…',
    flightBookingPending:
      "I'm booking {{time}} to {{place}}. I'll let you know when it's done.",
    flightBookingConfirmed:
      "I've booked {{time}} to {{place}} on {{date}}.{{reference}}",
    flightBookingUnknown:
      "I'm checking whether {{time}} to {{place}} went through. I'll let you know.",
    flightBookingNotBooked:
      "I couldn't book {{time}} to {{place}}. I'll message you about what to do next.",
    hotelBookingPending:
      "I'm booking {{name}}. I'll let you know when it's done.",
    hotelBookingConfirmed: "I've booked {{name}}.{{reference}}",
    hotelBookingUnknown:
      "I'm checking whether {{name}} went through. I'll let you know.",
    hotelBookingNotBooked:
      "I couldn't book {{name}}. I'll message you about what to do next.",
    sameTicket: 'Same ticket',
    confirmBooking: 'Confirm booking',
    traveller: 'Traveller',
    sort: {
      label: 'Sort by',
      recommended: 'Recommended',
      price: 'Price',
      departure: 'Departure time',
      arrival: 'Arrival time',
      duration: 'Duration',
      rating: 'Rating',
    },
  },
  suggestions: {
    loading: 'Loading…',
    linkExpiredTitle: 'Link expired',
    somethingOffTitle: 'Something’s off',
    expiredBody:
      'This link has expired. Ask your secretary on WhatsApp and they’ll send you a fresh one.',
    invalidBody:
      'This link isn’t valid. Please open the link from your WhatsApp conversation.',
    errorBody:
      'Something went wrong loading this page. Please try again in a moment.',
    fromAssistantNamed: 'From {{name}}, your secretary',
    fromAssistant: 'From your secretary',
    titleLead: 'Let’s',
    titleAccent: 'get started',
    coverCopy:
      'Six things people hand me every week — real examples from real users. Pick one and I’ll take it from here.',
    swipeUp: 'Swipe up',
    picked: 'Picked — I’ll be in touch',
    swipeNext: 'Swipe for the next one',
    thatsTheLot: 'That’s the lot — pick one ✦',
  },
  // Feature tour for people who joined before the new onboarding (6 Oct):
  // plays after the Morning List's cards, or on its own page (/wa-tour) if
  // they did not open the list. {{name}} is the assistant's name. Each scene
  // has a two-tone headline (headline in ink, tail softened), one line under
  // it, a short `name` for screen readers, and a `topic`: how the assistant
  // names it afterwards on WhatsApp ("You wanted to hear more about …").
  tour: {
    carouselAria: 'What’s new in Dream',
    whatsNew: 'What’s new',
    ofCount: '{{n}} of {{total}}',
    previous: 'Previous',
    startExploring: 'Start exploring',
    showMeAfter: 'Show me after',
    addedAfterList: 'Great, I’ll tell you about this',
    addedForWhatsApp: 'Added — I’ll pick it up on WhatsApp',
    added: 'Added',
    notInterested: 'I’m not interested in this',
    okThanks: 'Ok, thanks',
    doneTitle: 'See you on WhatsApp',
    doneBody:
      'I’ll pick up anything you asked about in our chat. You can close this page.',
    openWhatsApp: 'Back to WhatsApp',
    linkExpiredTitle: 'This link has expired',
    somethingOffTitle: 'Something’s off',
    expiredBody:
      'This tour link is no longer live. Just message your secretary on WhatsApp and ask about the new features.',
    invalidBody:
      'This link isn’t valid. Please open the link from your WhatsApp conversation.',
    errorBody: 'Something went wrong. Please try again in a moment.',
    scenes: {
      summary: {
        name: 'Summary',
        headline: 'That’s what’s new',
        tail: 'for now.',
        line: 'Anything ticked, I’ll pick up with you on WhatsApp.',
      },
      intro: {
        name: 'Introduction',
        headline: '{{name}} has',
        tail: 'some new skills.',
        line: 'Here are {{count}} new things from {{name}} for you to try out.',
      },
      scheduling: {
        name: 'Scheduling',
        headline: 'Never chase',
        tail: 'a meeting time again.',
        line: 'Tell me who you need to see and roughly when. I’ll agree a time with them and put it in your calendar.',
        topic: 'booking meetings',
      },
      assistants: {
        name: 'Secretary to Secretary',
        headline: 'Meetings with colleagues,',
        tail: 'agreed between secretaries.',
        line: 'If they use Dream too, our secretaries sort the time out between themselves. Only scheduling is shared — nothing else in your calendar.',
        topic: 'talking to other people’s secretaries',
      },
      shop: {
        name: 'Weekly shop',
        headline: 'Your Ocado shop,',
        tail: 'planned and in the basket.',
        line: 'Connect Ocado and tell me what the week looks like. I’ll plan the meals, fill the basket and check with you before it’s booked.',
        topic: 'your weekly Ocado shop',
      },
      email: {
        name: 'Email on WhatsApp',
        headline: 'Reply to email',
        tail: 'without opening it.',
        line: 'I’ll draft replies in your voice and show them to you here. Tap Send, or open the draft to change it first.',
        topic: 'replying to email from WhatsApp',
      },
      check_in: {
        name: 'Evening check-in',
        headline: 'A short check-in',
        tail: 'at 7pm.',
        line: 'What got done today, what’s still open and what tomorrow looks like — so nothing slips overnight.',
        topic: 'the 7pm check-in',
      },
      organisations: {
        name: 'Organisations',
        headline: 'Bring Dream',
        tail: 'to your company.',
        line: 'Set up Dream for your whole team. Add secretaries for your key staff, all billed through one card — and I’ll help them get started.',
        topic: 'Dream for your organisation',
      },
    },
  },
  briefing: {
    linkExpiredTitle: 'This list has closed',
    somethingOffTitle: "Something's off",
    expiredBody:
      'This Morning List is from an earlier day. Anything still open will come round again — or just message your secretary on WhatsApp.',
    invalidBody:
      "This link isn't valid. Please open the link from your WhatsApp conversation.",
    errorBody: 'Something went wrong. Please try again in a moment.',
    brandAria: 'Dream',
    listAria: 'Your briefing',
    navAria: 'Briefing navigation',
    optionsAria: 'Actions for this',
    chooseAnother: 'Choose a different action',
    openDraft: 'Open draft',
    copyDraft: 'Copy draft',
    copied: 'Copied',
    openLinkedin: 'Open in LinkedIn',
    openWhatsapp: 'Open WhatsApp',
    draftOnMac: 'Also in WhatsApp on your Mac',
    notRelevant: 'Not relevant',
    notRelevantAria: 'This isn’t relevant',
    chosen: 'Chosen: {{label}}',
    next: 'Next',
    seeList: 'See your to-do list',
    previous: 'Previous item',
    whyPrompt:
      'You have skipped things like “{{headline}}” before. A word on why helps me stop bringing them up.',
    whyPlaceholder: 'Why isn’t this relevant?',
    whySkip: 'Skip',
    whySend: 'Send',
    whyThanks: 'Thanks — noted.',
    todosAria: 'Your to-dos',
    today: 'Today',
    tomorrow: 'Tomorrow',
    weekAria: 'Pick a day',
    nothingPlanned: 'Nothing planned.',
    clearDay: 'Clear day.',
    clearDayName: 'Clear day, {{name}}.',
    nothingDueToday: 'Nothing due today.',
    getAhead: 'Get ahead',
    noDate: 'No date',
    doToday: 'Do today',
    doTodayAria: 'Do {{title}} today',
    spottedInEmail: 'Spotted in your email',
    spottedCount_one: '{{count}} thing you might want to add',
    spottedCount_other: '{{count}} things you might want to add',
    sinceDay: 'since {{day}}',
    dueToday: 'Due today',
    dueTomorrow: 'Due tomorrow',
    dueOn: 'Due {{date}}',
    addToToday: 'Add to today',
    addToTomorrow: 'Add to tomorrow',
    addFor: 'Add for {{date}}',
    addToCalendar: 'Add to calendar',
    addedToTomorrow: 'Added to tomorrow.',
    addedFor: 'Added for {{date}}.',
    addedToCalendar: 'Added to your calendar.',
    addedToCalendarAt: 'In your calendar: {{date}}, {{time}}.',
    notNow: 'Not now',
    doneAria: 'Mark {{title}} done',
    actionsFor: 'Actions for {{title}}',
    delegate: 'Delegate',
    findTime: 'Find time',
    assistantFallback: 'Dream',
    notePlaceholder: 'Any instructions?',
    noteAria: 'Note for {{name}} about {{title}}',
    delegateSend: 'Hand it to {{name}}',
    pickHint: 'Tap a to-do to hand it off or find it a time',
    pickedFor: 'For: {{title}}',
    recordVoiceNote: 'Record a voice note',
    stopRecording: 'Stop recording',
    listening: 'Listening…',
    transcribing: 'Writing that down…',
    delegationQueued: 'Queued for {{name}}',
    delegationWorking: '{{name}} is working on it',
    micUnavailable: 'The microphone isn’t available here.',
    lookingForTime: 'Looking at your day…',
    howAbout: 'How about {{time}}?',
    howAboutContext: 'How about {{time}}, {{context}}?',
    bookIt: 'Book it',
    otherTimes: 'Other times',
    partMorning: 'Morning',
    partAfterLunch: 'After lunch',
    partAfternoon: 'Afternoon',
    partEndOfDay: 'End of day',
    booked: 'Booked {{time}} in your calendar.',
    noCalendar: 'No calendar is connected, so there’s nothing to book into.',
    noFreeTime: 'No free time left today.',
    noFreeTimeInPart: 'No free time in that part of the day.',
    calendarUnavailable: 'Your calendar isn’t reachable right now.',
  },
  booking: {
    noKey: 'No booking key provided',
    invalidData: 'Invalid booking data received',
    loadFailed:
      'Failed to load booking information. The link may be expired or invalid.',
    loading: 'Loading your booking information...',
    errorTitle: 'Booking Link Error',
    goHome: 'Go to Homepage',
    noDataTitle: 'No Booking Data',
    noDataBody: 'Unable to load booking information.',
    title: 'Complete Your Flight Booking',
    redirectBody:
      "You're about to be redirected to complete your flight booking.",
    provider: 'Booking Provider',
    providerFallback: 'Flight Booking Provider',
    redirectingIn: 'Automatically redirecting in {{seconds}} seconds...',
    continueTo: 'Continue to {{provider}}',
    bookingSiteFallback: 'Booking Site',
    disclaimer:
      "You will be redirected to the booking provider's website to complete your purchase. This link is secure and will expire after 24 hours.",
  },
  artifact: {
    proofTeamSaw: 'What our team saw',
    proofScreenshotOf: 'Screenshot {{n}} of {{total}}',
    proofEmailHeading: 'Confirmation email',
    proofEmailReceived: 'Received {{when}}',
    proofPrivate: 'Only people with this link can see this page.',
    loading: 'Loading...',
    unableToLoad: 'Unable to load',
    noShareToken: 'No share token provided',
    loadFailed: 'Failed to load artifact',
    gettingLivePrices: 'Getting live prices',
    linkExpired: 'This link has expired. Please ask for a new one.',
    linkInvalid: 'This link is invalid or the item no longer exists.',
    genericError: 'Something went wrong. Please try again later.',
    sessionExpired:
      "This session expired because you weren't logged in. Please ask Dream to send the link again.",
    linkExpiredShort: 'This link has expired.',
    alreadyResponded: 'This has already been responded to.',
    submitFailed: 'Failed to submit response. Please try again.',
    responseSubmitted: 'Response submitted',
    responseSubmittedBody:
      'Your response has been recorded. You can close this page.',
    bookingNow: 'Booking now',
    bookingNowBody:
      "Hang on — this is being booked for you. You'll get the confirmation in chat; you can close this page.",
    booked: 'Booked',
    bookedBody:
      'All done. The confirmation is in your chat. You can close this page.',
    bookedReference: 'Reference {{reference}}',
    notBooked: 'Not booked',
    notBookedBody:
      'Nothing was charged. Dream has been told and will pick this up with you in chat.',
    bookingUnknown: 'Checking the booking',
    bookingUnknownBody:
      "The booking couldn't be confirmed straight away. Dream is checking with the operator and will update you in chat — nothing else is needed from you.",
    paymentReceived: 'Payment received',
    paymentReceivedBody:
      "We're confirming your payment — your credits will be added in a moment. You can close this page.",
    topUpComplete: 'Top-up complete',
    topUpCreditsAdded:
      '{{credits}} credits have been added to your account. You can close this page.',
    topUpAlreadyDone:
      'This top-up has already been completed. You can close this page.',
    topUpMissingDetails:
      'This top-up link is missing its details. Please ask for a new one.',
    checkoutFailed: 'Failed to start checkout. Please try again.',
    linkExpiredAskDream:
      'This link has expired. Please ask Dream to send a new one.',
    topUpAlreadyCompleted: 'This top-up has already been completed.',
    topUpFailed: 'Failed to process top-up. Your card was not charged.',
    continueSecureCheckout: 'Continue to secure checkout',
    cardSetupFailed: 'Failed to start card setup. Please try again.',
    cardAdded: 'Card added',
    cardAddedBody:
      'Your card is saved. Your secretary can now top up your card when you approve a purchase. You can close this page.',
    subscriptionActive: 'Subscription active',
    subscriptionActiveBody:
      'Your plan is set up and your credits have been added. You can close this page.',
    subscriptionPendingBody:
      "We're confirming your subscription — your credits will be added in a moment. You can close this page.",
    selectPlan: 'Select a plan',
    popular: 'POPULAR',
    yourPlan: 'Your plan',
    loadingShort: 'Loading...',
    choosePlan: 'Choose {{name}}',
    addCardOnly: 'Or just add a card without a plan',
    noContent: 'No content available.',
    alreadyHandled: 'Already handled',
    alreadyHandledTitled:
      '"{{title}}" has already been responded to, so no action is needed here. You can close this page.',
    alreadyHandledUntitled:
      'This request has already been responded to, so no action is needed here. You can close this page.',
    secureLoginCancelled: 'Sign-in cancelled',
    secureLoginCancelledLead: 'Sign-in',
    secureLoginCancelledAccent: 'cancelled',
    secureLoginDone: 'Signed in',
    secureLoginDoneAccent: 'securely',
    secureLoginDoneBody:
      'Dream can continue with your sign-in. You can close this page.',
    secureCardSaved: 'Card saved',
    secureCardSavedAccent: 'securely',
    secureCardSavedBody:
      'Your card is stored securely and never shared with the secretary. You can close this page.',
    secureLoginCancelledBody:
      "No details were saved. You can close this page and ask your secretary again whenever you're ready.",
  },
  wizard: {
    stepLabelPrivacy: 'Your privacy',
    mobileStepPrivacy: 'Privacy',
    getStarted: 'Get started',
    close: 'Close',
    privacyStep: {
      title: 'Give away the boring stuff, not your data',
      lede: 'A world-class secretary that handles your day-to-day, and keeps your personal information safe.',
      point1: 'Your data isn’t used to train our models',
      point2: 'Your data is never sold to anyone else',
      point3:
        'When you connect an app you can always review how your secretary uses it',
      point4:
        'Deleted means deleted. Your apps, your account, if it should be gone it’s gone',
      point5: 'No email you receive is stored by Dream',
      learnMore: 'I want to learn more',
      analyticsTitle: 'Help make your secretary better',
      analyticsBody:
        'Help us review how your secretary is performing. Findings are anonymous, nobody reads your conversations.',
      modalTitle: 'Your privacy, in plain English',
      rights: {
        memoryTitle: 'The right to manage your secretary’s memory',
        memoryBody:
          'You can see important information your secretary remembers about you. You can correct it or ask them to remove it.',
        conversationTitle: 'The right to a private conversation',
        conversationBody:
          'What you tell your secretary is private. Any conversation with your secretary will only be shared when compelled to do so by law. The Dream technical engineers can only review conversations when you have reported a bug and you will be informed.',
        dataUseTitle: 'The right to know how your data is used',
        dataUseBody: 'We do not sell your personal information.',
        knowTitle: 'The right to know what Dream knows about you',
        knowBody:
          'You can ask us what personal information we have about you and get a copy of it.',
        correctTitle: 'The right to correct what Dream gets wrong',
        correctBody:
          'If information we have about you is wrong, you can ask us to correct it.',
        deleteTitle: 'The right to delete your data',
        deleteBody:
          'You can ask us to delete your personal information. We may need to keep some information if the law requires us to.',
        portabilityTitle: 'The right to take your data with you',
        portabilityBody:
          'You can ask us for a copy of your personal information in a format you can use elsewhere.',
        limitTitle: 'The right to limit how your data is used',
        limitBody:
          'To the fullest extent possible to provide the app, you can ask us to stop or limit how we use your personal information.',
        accessTitle: 'The right to know what your secretary can access',
        accessBody:
          'You can see which accounts and services your secretary can access. You can remove that access when you want to.',
        providersTitle: 'The right to know when someone else needs your data',
        providersBody:
          'Dream relies on some technology providers. They are audited for GDPR compliance.',
        securityTitle: 'The right to reasonable security',
        securityBody:
          'We take steps to keep your personal information safe and stop people accessing it without permission.',
      },
    },
    stepLabel1: 'Connect your accounts',
    stepLabel2: 'Daily Briefing',
    stepLabel3: 'Integrations',
    stepLabel4: 'Your secretary',
    stepLabel5: 'First tasks',
    mobileStep1: 'Accounts',
    mobileStep2: 'Briefing',
    mobileStep3: 'Integrations',
    mobileStep4: 'Secretary',
    mobileStep5: 'First tasks',
    briefingSub1: 'What it is',
    briefingSub2: 'When it lands',
    briefingSub3: 'Other routines',
    assistantSub1: 'Name',
    assistantSub2: 'Email',
    assistantSub3: 'Card',
    assistantSub4: 'The team',
    taskSub1: 'Already done',
    taskSub2: 'Referrals',
    taskSub3: 'Your first task',
    continue: 'Continue',
    letsGetStarted: 'Let’s get started',
    back: 'Back',
    stepOf: '{{label}} · {{step}} of {{total}}',
    eyebrow: 'Step {{step}} of {{total}} · {{label}}',
    oneMoment: 'One moment…',
    loadingSetup: 'Loading your setup…',
    linkErrorTitle: 'Hmm, that link didn’t work',
    missingToken:
      'This link is missing its token. Ask your secretary for a fresh one.',
    loadFailed: 'Something went wrong loading your setup.',
    genericError: 'Something went wrong. Try again.',
    allSetTitle: 'You’re all set',
    allSetBody:
      'Head back to your WhatsApp chat with {{name}} — they’re ready for you.',
    openWhatsApp: 'Open WhatsApp',
    privacy: 'Privacy',
    terms: 'Terms',
    openingWhatsAppToast: 'Opening WhatsApp — “{{task}}” with {{name}}.',
    oauthMissingScopes:
      'Your account is connected. {{scopes}} access was not granted, so your secretary won’t be able to help with {{area}} on this account. You can grant access at any time by reconnecting the account.',
    oauthMissingAreaSingle: 'that area',
    oauthMissingAreaMulti: 'those areas',
    oauthNoMailboxTitle: 'Connected — but this account has no Gmail inbox',
    oauthNoMailbox:
      'Google reports that Gmail isn’t enabled for this account (its Workspace no longer includes Gmail, or it’s a Google login for a non-Gmail address). Your secretary can use its calendar and contacts, but won’t be able to read, search or draft email from it. If your email lives somewhere else, connect that account too.',
    listAnd: 'and',
    oauthAlreadyLinkedOther:
      'That email account is already linked to a different account, so it could not be added here. Connect a different one, or contact us if you think this is wrong.',
    oauthExpired:
      'This setup link has expired. Ask your secretary on WhatsApp for a fresh one.',
    oauthInvalidToken:
      'This setup link is no longer valid. Ask your secretary on WhatsApp for a fresh one.',
    oauthError:
      'Something went wrong connecting that account. Please try again.',
    halfModalTitle: 'Dream can only see half your week',
    halfModalWork:
      'Without your work calendar it’ll offer times you’re not actually free, and miss the things that matter most.',
    halfModalPersonal:
      'Without your personal calendar it’ll book over your evenings and weekends as if they were free.',
    connectWork: 'Connect work',
    connectPersonal: 'Connect personal',
    carryOnAnyway: 'Carry on anyway',
    noAccount: {
      title: 'Continue without an account?',
      needs:
        '{{name}} needs to see what’s on your calendar and email to do their best work.',
      impact:
        'They won’t be able to check your availability and this could cause clashes on your schedule.',
      changeLater:
        'If you’re sure, choose ‘continue’ below. You can change your mind later.',
      understandContinue: 'I understand, continue',
      goBackConnect: 'Go back and connect',
    },
    previewCap: 'What lands at',
    previewMorning:
      'Morning{{name}} — here’s your day. Three meetings, two replies still owed.',
    previewFullBriefing: 'Full briefing on every meeting',
    previewEveningRoundUp: 'Evening round-up of loose ends',
    previewOwedReplies: 'Replies you owe, and are owed',
    connect: {
      title: 'Connect your accounts',
      ledeWide:
        'Connect your email and calendar so {{name}} can keep on top of your tasks, and watch for calendar clashes and conflicts.',
      ledeNarrow:
        'Connect your email and calendar so {{name}} can see your whole week.',
      ledeExtra:
        'Add your personal and your work account and they’ll see your whole week.',
      personal: 'Personal',
      personalSub: 'Your own inbox and diary.',
      work: 'Work',
      workSub: 'Where most of your meetings live.',
      connected: 'Connected',
      notConnected: 'Not connected',
      connectedAccount: 'Connected account',
      disconnectAria: 'Disconnect {{account}}',
      thisAccount: 'this account',
      addAnother: 'Add another',
      opening: 'Opening…',
      openConnectError: 'Unable to open the connect page.',
      disconnectError: 'Unable to disconnect account.',
      adminNoteTitle: 'Couldn’t connect your work account?',
      adminNoteBody:
        'If you were sent straight back without a consent screen, it may be that your workspace admin needs to approve Dream first — here’s a guide you can send them.',
      adminGuideShareTitle: 'Why Dream needs access to your Google account',
      sendAdminGuide: 'Send your admin the guide',
      guideLinkCopied: 'Link copied — paste it to your admin',
      viewGuide: 'View the guide',
      dismiss: 'Dismiss',
      connectionDidntFinish: 'That connection didn’t finish',
      neverSend: 'They’ll never send an email from your account.',
      disclaimer: 'We support Gmail, Google Workspace and Outlook accounts.',
      noAccountHint: 'Prefer not to connect one?',
      carryOnWithout: 'Carry on without an email account',
      chooseTitle: 'Connect an email account',
      chooseLede:
        'Gmail or Outlook. I’ll tell from the address whether it’s work or personal, and you can change it.',
      gmail: 'Gmail',
      outlook: 'Outlook',
      workNeededNote: '{{org}} needs your work email, so connect that one.',
      kindAria: 'Work or personal',
      changeKindError: 'Couldn’t change the account type. Try again.',
      kindLabel: 'What kind of account is it?',
      professional: 'Professional',
      professionalSub: 'Your work email and calendar.',
      neededFor: 'Needed for {{org}}',
      providerLabel: 'Which provider is it with?',
      google: 'Google',
      microsoft: 'Microsoft',
      other: 'Other',
      otherNoteWork:
        '{{org}} needs a Google or Microsoft work account. If your work email is with another provider, ask your IT team.',
      otherNote:
        '{{name}} works with Google and Microsoft accounts for now. You can carry on without one and connect later.',
    },
    briefing: {
      whatTitle: 'Wake up already caught up',
      whatLedeWide:
        'Every morning you’ll get one message with everything you’d otherwise find out too late.',
      whatLedeNarrow: 'One message every morning, on WhatsApp.',
      morningLede: 'Morning{{name}} — here’s your day.',
      line1: 'Flight to Amsterdam, 11:05 — boarding pass attached',
      line2: 'Three meetings, all briefed with photos',
      line3: 'Two replies still owed, drafts ready',
      line4: 'Angie’s birthday on Thursday',
      whatFoot: 'Lands on WhatsApp, every morning.',
      whenTitle: 'When should it land?',
      whenLedeWide:
        'Pick a time that suits you — you can move it whenever you like.',
      whenLedeNarrow: 'Pick a time — you can move it later.',
      arrivesAt: 'Arrives at',
      onWhatsApp: 'On WhatsApp',
      earlier15: '15 minutes earlier',
      later15: '15 minutes later',
      deliveryTimeAria: 'Briefing delivery time',
      includeInBriefing: 'Include in the briefing',
      fullMeetingBriefing: 'Full meeting briefing',
      alwaysOn: 'Always on',
      fullMeetingSub:
        'Photos, how you were introduced, and how to pronounce everyone’s name.',
      routinesTitle: 'More your secretary can run',
      routinesLede: 'All on to start with — turn off anything you don’t want.',
      eveningTitle: 'Evening round-up of loose ends',
      eveningSub:
        'A short message at the end of the day: what slipped, what you said you’d do, and what’s waiting for you in the morning.',
      owedTitle: 'Tracking owed replies',
      owedSub:
        'They keep count of who’s waiting on you and who you’re waiting on, and nudge you before it gets embarrassing.',
      labellingTitle: 'Sorting and labelling your inbox',
      labellingSub:
        'Tags what lands — receipts, newsletters, things that need you — and moves the noise out of your way. Already keep your inbox in order? Leave this off.',
    },
    integrations: {
      title: 'Plug Dream into everything else',
      ledeWide:
        '{{name}} can work inside the tools you already use, reading what’s there and doing the parts you’d otherwise do by hand.',
      ledeNarrow: '{{name}} works inside the tools you already use.',
      ledeExtra: 'Connect what you like now, or come back to any of it later.',
      alreadyConnected: 'Already connected',
      whatsappRowName: 'WhatsApp',
      whatsappRowMeta: 'Where you and {{name}} talk',
      spottedInEmail: 'Spotted in your email',
      everythingElse: 'Everything else',
      addMore: 'Add more',
      showFewer: 'Show fewer',
      showAll: 'Show all {{count}}',
      connected: 'Connected',
      connect: 'Connect',
      comingSoon: 'Coming soon',
      cancel: 'Cancel',
      close: 'Close',
      verifying: 'Verifying…',
      personal: 'Personal',
      work: 'Work',
      connectedAccount: 'Connected account',
      connectApp: 'Connect {{name}}',
      apiKeyBody: '{{hint}} and {{name}} will verify it before saving.',
      apiKeyDefaultHint: 'Paste your API key',
      apiKeyPlaceholder: '{{name}} API key',
      connectError: 'Could not connect. Try again.',
      apps: {
        slack: {
          sub: 'Messages and channels',
          spottedSub:
            'Watch the channels that matter, post updates on your behalf, and catch you up on what you missed.',
        },
        hubspot: {
          sub: 'Contacts and deals',
          spottedSub:
            'Log calls and notes, keep deals and contacts current, and chase the next steps for you.',
        },
        attio: {
          sub: 'CRM records',
          spottedSub:
            'Keep records and new contacts current after every call and email, without you doing the admin.',
        },
        linear: {
          sub: 'Issues and projects',
          spottedSub:
            'Raise, update and chase issues so your projects keep moving while you are elsewhere.',
        },
        notion: {
          sub: 'Pages, lists and databases',
          spottedSub:
            'Read your shared to-do lists, add and tick off items, and create or update pages and databases for you.',
        },
        asana: {
          sub: 'Tasks and projects',
          spottedSub:
            'Create and update tasks from your conversations and chase whatever is blocking a project.',
        },
        todoist: {
          sub: 'Your to-do list',
          spottedSub:
            'Add to-dos straight from email and WhatsApp, and tick them off as they get done.',
        },
        granola: {
          sub: 'Meeting notes',
          spottedSub:
            'Turn your meeting notes into actions and follow-ups automatically after every call.',
        },
        xero: {
          sub: 'Invoices and bills',
          spottedSub:
            'Chase unpaid invoices, file receipts and keep an eye on bills without you opening it.',
        },
        lusha: {
          sub: 'Contact enrichment',
          spottedSub:
            'Find contact details for the people you want to reach, right when you need them.',
          keyHint: 'Paste your Lusha API key',
        },
        devin: {
          sub: 'Engineering tasks',
          spottedSub:
            'Hand over engineering jobs, track them to done, and get told when they need you.',
          keyHint: 'Paste your Devin API key',
        },
      },
    },
    assistant: {
      nameTitle: 'Meet your secretary',
      nameLedeWide:
        'They’ll introduce themselves by this name. Change it if you’d rather call them something else.',
      nameLedeNarrow: 'Change it if you’d rather call them something else.',
      nameLabel: 'Secretary’s name',
      nameRequired: 'Give your secretary a name.',
      emailTitle: '{{name}} has their own email address',
      emailLede:
        'They can speak to the world on your behalf — and will only ever answer to you.',
      sigQuote:
        '…I’ve put three options in for Thursday. Let me know which suits and I’ll get it confirmed.',
      sigRole: 'Secretary',
      sigRoleTo: 'Secretary to {{name}}',
      whenEmail: 'When can they email?',
      expressPermission: 'Only when I give express permission',
      cardTitle: 'And their own card',
      cardLede:
        'For booking and buying things on your behalf. It never spends without your say-so.',
      cardKind: 'Virtual · Single use',
      cardholder: 'Cardholder',
      expires: 'Expires',
      whenSpend: 'When can they spend?',
      teamTitle: 'There are people behind {{name}}',
      teamLedeWide:
        'Some things can’t be done by software. When that happens, a trained person on our team picks it up — and {{name}} stays the one talking to you.',
      teamLedeNarrow:
        'When software can’t do it, a real person picks it up. {{name}} stays the one talking to you.',
      whatTheyDo: 'What they’ll do',
      teamItem1:
        'Phone a restaurant, a clinic or a venue that has no booking system',
      teamItem2: 'Work through a website that fights automation',
      teamItem3: 'Chase something that needs a human to keep asking',
      playSample: 'Play sample call',
      pauseSample: 'Pause sample call',
      hearCallIn: 'Hear the call in',
      callLanguage: {
        en: 'English',
        de: 'German',
        fr: 'French',
        it: 'Italian',
        es: 'Spanish',
      },
      audioTitle: 'Hear a real call, booking a table',
    },
    alreadyDone: {
      putToWorkTitle: 'Put {{name}} to work',
      putToWorkLede:
        'First job: drafting replies and follow-ups for whatever’s waiting in your inbox.',
      firstDrafts: 'Your first drafts',
      firstDraftsSub: 'Ready in a few minutes. {{name}} never sends — you do.',
      draftCta: 'Draft my first replies',
      hitASnag: '{{name}} hit a snag',
      snagLede: 'I couldn’t finish your first drafts — no harm done.',
      snagLine1: 'I keep drafting as new email comes in',
      snagLine2: 'Nothing sends without you',
      tryingAgain: 'Trying again…',
      tryAgain: 'Try again',
      alreadyDoneTitle: 'Already done',
      emptyLede: 'All caught up — nothing needed a reply just now.',
      emptyLine1: 'I draft as new email comes in',
      emptyLine2: 'Everything waits in your drafts folder',
      readyLede: 'Done — your drafts are ready.',
      readyLine1: 'Waiting in your drafts folder',
      readyLine2: 'Edit them yourself, or ask me for changes',
      onItTitle: '{{name}} is on it',
      now: 'now',
      working: 'Working',
      workingLede:
        'I’m going through your inbox now — your first drafts take a few minutes.',
      workingLine1:
        'They’ll wait in your drafts folder — nothing sends without you',
      workingLine2: 'I’ll message you on WhatsApp the moment they’re ready',
      workingFoot: 'No need to wait here — feel free to carry on.',
    },
    referrals: {
      title: 'Refer friends, get more done',
      lede1: 'Get started with {{amount}} of credits a month, free.',
      lede2: 'Refer a friend, you get {{amount}} of credits and so do they.',
      lede1V3: 'Your first month of Dream is free — unlimited use, on us.',
      lede2V3: 'Invite a friend and you both get bonus usage on Dream.',
      invitesNote:
        'You’ve got {{count}} invites this month, choose someone who really needs a secretary.',
      yourInviteLink: 'Your invite link',
      copied: 'Copied',
      copyLink: 'Copy link',
      copyError: 'Could not copy your link — select it and copy.',
    },
    tasks: {
      loaderTitle: 'Getting to know you',
      loaderLede:
        'Give me a moment with your inbox and calendar. Here’s what I’ve picked up so far.',
      spline1: 'Reading the rhythm of your week',
      spline2: 'Understanding your travel patterns',
      spline3: 'Figuring out when you prefer meetings',
      spline4: 'Spotting the people you talk to most',
      spline5: 'Learning which emails actually matter to you',
      spline6: 'Noticing your regular bookings and haunts',
      spline7: 'Mapping your mornings and your late nights',
      spline8: 'Picking up on subscriptions and renewals',
      title: 'What should {{name}} do first?',
      lede: 'Pick one. You’ll finish it off in the chat.',
      pickOne: 'Pick one to start with',
      chooseAria: 'Choose a first task',
      somethingElse: 'Something else',
      somethingElseTagline: 'You can ask for anything',
      bookRestaurant: 'Book a restaurant',
      bookHaircut: 'Book a haircut',
      findFlights: 'Find flights',
      yourFirstTask: 'your first task',
      validation: 'Pick one to get started.',
      handoff: 'Opens your WhatsApp chat with {{name}}.',
    },
  },
  // Public guest view of a shared to-do list (/to-do-list/guest/:token).
  // Reached from an email/WhatsApp invite by someone who is not on Dream.
  // The page shows one list; the guest can add tasks to it and tick/untick
  // tasks assigned to them or added by them. "PA" = Personal Assistant (the
  // Dream product).
  login: {
    title: 'Sign in to Dream',
    lede: "Enter the phone number you use with Dream and we'll text you a one-time code.",
    phoneLabel: 'Phone number',
    countryLabel: 'Country code',
    countrySearchPlaceholder: 'Search countries',
    countryNoResults: 'No countries match',
    sendCode: 'Send me a code',
    or: 'or',
    continueWithGoogle: 'Continue with Google',
    continueWithMicrosoft: 'Continue with Microsoft',
    newHere:
      'New to Dream? Start a conversation with your secretary on WhatsApp first.',
    errors: {
      empty: 'Enter your phone number.',
      malformed:
        "That doesn't look like a phone number. Use digits only, e.g. {{selectedCode}} 7700 900123.",
      tooShort:
        'That number is too short for {{selectedCountry}} ({{selectedCode}}).',
      tooLong:
        'That number is too long for {{selectedCountry}} ({{selectedCode}}).',
      countryMismatch:
        'That number starts with {{typedCode}} ({{typedCountry}}) but {{selectedCountry}} ({{selectedCode}}) is selected. Pick {{typedCountry}} in the dropdown or remove the prefix.',
      invalidForCountry:
        "That isn't a valid {{selectedCountry}} number. Check it and try again.",
    },
  },
  todoGuest: {
    // Browser tab title.
    pageTitle: 'Shared to-do list · Dream',
    // Blue banner under the Dream logo. {{inviter}} = person who shared the
    // list, {{list}} = list name; both wrapped in <strong>.
    banner:
      '<strong>{{inviter}}</strong> invited you to edit the <strong>{{list}}</strong> list with Dream',
    // Pill under the list title showing how many people are on the list.
    members_one: '{{count}} member',
    members_other: '{{count}} members',
    // Tooltip on the guest's own avatar in the members pill.
    you: '{{name}} (you)',
    // Shown in the task list when there is nothing to do.
    empty: 'Nothing on this list yet.',
    // Heading of the section listing tasks the guest has already ticked off
    // (kept visible so an accidental tick can be undone).
    completedByYou: 'Completed by you',
    task: {
      // Small grey text on a task row: who added the task.
      from: 'from {{name}}',
      fromGuest: 'from {{name}} (guest)',
      editedBy: 'edited by {{name}}',
      // Assignee chip on a task row when the task is assigned to the guest.
      assignedToYou: 'You',
      // Assignee chip on a task row when nobody is assigned.
      unassigned: 'Unassigned',
      // Accessible label of the circle button on a task assigned to the guest.
      complete: 'Mark as done',
      // Accessible label of the circle on a task the guest already completed.
      reopen: 'Mark as not done',
      // Accessible label of the (disabled) circle on someone else's task.
      notYours: 'Only the person this task is assigned to can tick it off',
    },
    // Adding a task as a guest. `button` is the small pill beside the list
    // title; the rest is the bottom sheet it opens (form with a text field,
    // due date, who it's for, and priority).
    newTask: {
      button: 'New',
      title: 'New task',
      descriptionLabel: 'Task',
      descriptionPlaceholder: 'Describe your task',
      // Chip (with a calendar icon) that opens the date picker; replaced by
      // the chosen date once set.
      dueDateLabel: 'Add due date',
      // Chip (with a person icon) that opens the list-member picker; replaced
      // by the chosen member's name once set.
      assignLabel: 'Assign',
      // First option in that picker: the task is for nobody in particular.
      unassigned: 'Unassigned',
      // Chip (with a grey dot) that opens the priority picker.
      priorityLabel: 'Add priority',
      // First option in that picker: clears the priority.
      noPriority: 'No priority',
      // Picker options, also shown on the chip (with a coloured dot) once set.
      priority: {
        high: 'High priority',
        medium: 'Medium priority',
        low: 'Low priority',
      },
      // Primary button at the bottom of the sheet, and its in-flight label.
      submit: 'Add task',
      submitting: 'Adding…',
      // Shown in the sheet when the request fails for a non-validation reason.
      error: "Couldn't add that task. Please try again.",
    },
    errors: {
      // Inline error above the task list when a tick/untick fails.
      updateFailed: "Couldn't update that task. Please try again.",
    },
    // One-time-code screen shown before a guest can open someone else's shared
    // to-do list in a browser we haven't seen: a 6-digit code is sent to the
    // WhatsApp number / email the invite went to. {{destination}} = masked
    // address (e.g. "+44 ••• ••• 789"), {{inviter}} = inviter's first name,
    // {{list}} = list name.
    verify: {
      titleWhatsApp: 'Check WhatsApp for your code',
      titleEmail: 'Check your email for your code',
      bodyWhatsApp:
        'To open <strong>{{inviter}}</strong>’s list <strong>{{list}}</strong>, enter the 6-digit code we just sent to <strong>{{destination}}</strong> on WhatsApp.',
      bodyEmail:
        'To open <strong>{{inviter}}</strong>’s list <strong>{{list}}</strong>, enter the 6-digit code we just sent to <strong>{{destination}}</strong>.',
      codeLabel: 'Verification code',
      submit: 'Open the list',
      checking: 'Checking…',
      sending: 'Sending your code…',
      // Countdown under the code field before a new code can be requested.
      resendIn_one: 'Resend code in {{count}} second',
      resendIn_other: 'Resend code in {{count}} seconds',
      resend: 'Resend code',
      sendCode: 'Send me a code',
      errors: {
        invalid: 'That code isn’t right. Try again.',
        invalidWithAttempts_one:
          'That code isn’t right. {{count}} attempt left.',
        invalidWithAttempts_other:
          'That code isn’t right. {{count}} attempts left.',
        expired: 'That code has expired. Request a new one.',
        tooManyAttempts: 'Too many wrong attempts. Request a new code.',
        sendFailed: 'We couldn’t send your code just now. Please try again.',
        generic: 'Something went wrong. Please try again.',
      },
    },
    // Card shown instead of the list when the link is expired or revoked.
    // "Assistant" = the Dream Personal Assistant product; "lists" = shared
    // to-do lists like the one this link pointed at.
    invalid: {
      title: "This link has expired. You'll need to be invited again.",
      body: 'Get your own secretary to access lists, and do even more.',
      // Dark one-line button under the body; opens a WhatsApp chat with Dream.
      cta: 'Sign up now on WhatsApp',
    },
    // Bottom sheet shown after the guest ticks off their first task, inviting
    // them to get their own Dream assistant.
    nudge: {
      title: 'Do even more with your own AI Secretary',
      body: 'Get a world-class secretary backed by trained real secretaries, ready to handle your day-to-day.',
      // Primary button (guests invited over WhatsApp): opens a WhatsApp chat.
      getYourOwnPa: 'Get your own secretary',
      // Guests invited by email instead enter their phone number so the
      // assistant can message them on WhatsApp. Label above the input.
      phonePrompt:
        'Start chatting to your secretary on WhatsApp — enter your phone number',
      phonePlaceholder: 'Your phone number',
      // Accessible label of the arrow button that submits the phone number.
      phoneSubmit: 'Continue on WhatsApp',
      phoneInvalid: 'Please enter a valid phone number including country code.',
      // Result shown in place of the phone form after submitting.
      outcome: {
        sent: 'Done — check WhatsApp, your new secretary has just messaged you.',
        already_on_dream:
          'That number already has a Dream account. Open WhatsApp to carry on chatting to your secretary.',
        already_invited:
          "You've already started signing up — open WhatsApp to pick up where you left off.",
      },
      error: 'Something went wrong. Please try again.',
      // Text link under the button/form that closes the sheet.
      later: 'Maybe later',
    },
  },
  // Expense review page (opened from the WhatsApp "Review" button, or the
  // inline card in the web chat): coded expenses waiting for the user's
  // go-ahead before they are written to the accounting system. "File" =
  // write to the accounting system with the receipt attached. Translation
  // brief with screen context and glossary: dream-be/docs/i18n-expenses.md.
  expenseReview: {
    headingSingle: 'Your reimbursement request',
    headingMulti: 'Your reimbursement requests',
    subSingle:
      "This has not been submitted yet. Choose 'submit' or ask your secretary when you're ready.",
    subMulti:
      "These have not been submitted yet. Choose 'submit' or ask your secretary when you're ready.",
    alreadySubmittedSingle: 'This expense has already been submitted.',
    alreadySubmittedMulti: 'These expenses have already been submitted.',
    empty: 'There are no expenses on this review.',
    // Footer buttons. `submitAll` / `submitSome` appear only with several expenses.
    submit: 'Submit',
    submitting: 'Submitting…',
    submitAll: 'Submit all {{count}}',
    submitSome: 'Submit {{selected}} of {{total}}',
    close: 'Close',
    notNow: 'Not now',
    error: "Couldn't reach Dream. Please try again.",
    // Per-expense card.
    include: 'Include {{vendor}}',
    thisExpense: 'this expense',
    unknownVendor: 'Unknown vendor',
    amountMissing: 'Amount missing',
    duplicate:
      'Looks like a duplicate of an expense already raised (ref {{ref}}).',
    rows: {
      // Shown next to the amount when the receipt was in a foreign currency.
      settledOnCard: '{{amount}} on the card',
      // Link to the filed expense in the accounting system, once it exists.
      inYourAccounts: 'Open in your accounts',
    },
    paymentMethod: {
      company_card: 'Company card',
      personal_card: 'Personal card (reimbursement)',
    },
    // Status pill on each card.
    status: {
      draft: 'Draft',
      awaiting_info: 'Needs more info',
      awaiting_confirmation: 'Ready to file',
      approved: 'Approved',
      submitted: 'Filed',
      failed: 'Filing failed',
      discarded: 'Discarded',
    },
    receipts: {
      none: 'No receipt attached',
      fallbackName: 'Receipt {{n}}',
      openInDream: 'Open Dream to view the file',
    },
  },
};

export default en;
